import { useP2GMediaQueryOnly } from '@parcel2go/core-ui';
import { Outlet } from 'react-router-dom';
import { Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import { SettingsCache } from '~/lib/consts/settings-cache';
import DesktopTitledLayout from './desktop-layout';
import MobileLayout from './mobile-layout';
import Navvy from './navvy';
import useSubscriptionsEnabled from '~/hooks/use-subscriptions-enabled';

// Account area (account experience). Tabs surface existing components: My Details,
// My Plan (behind the tenant's subscriptions setting), Saved Addresses, Billing (the Invoices /
// Statements view) and - behind the 'StoredCards' feature flag - Stored Cards. Underline tab style
// (Navvy), no counts. API Keys / Tariff Codes are deferred.
export default function Component() {
  const isXs = useP2GMediaQueryOnly('xs');
  const { t } = useTranslation(TranslationNamespaces.MYACCOUNT);
  const subscriptionsEnabled = useSubscriptionsEnabled();

  const routes = useMemo(() => {
    const output = [
      ...(subscriptionsEnabled ? [RoutingConstants.MyPlan] : []),
      RoutingConstants.MyDetails,
      RoutingConstants.SavedAddresses,
      RoutingConstants.Billing
    ];
    if (SettingsCache.settings.Features.includes('StoredCards')) {
      output.push(RoutingConstants.StoredCards);
    }
    return output;
  }, [subscriptionsEnabled]);

  const labels = useMemo(
    () => ({
      [RoutingConstants.MyDetails]: t('ACCOUNT_TAB_MY_DETAILS', { defaultValue: 'My Details' }),
      [RoutingConstants.SavedAddresses]: t('ACCOUNT_TAB_SAVED_ADDRESSES', { defaultValue: 'Saved Addresses' }),
      [RoutingConstants.Billing]: t('ACCOUNT_TAB_BILLING', { defaultValue: 'Billing' }),
      [RoutingConstants.MyPlan]: t('ACCOUNT_TAB_MY_PLAN', { defaultValue: 'My Plan' }),
      [RoutingConstants.StoredCards]: t('ACCOUNT_TAB_STORED_CARDS', { defaultValue: 'Stored Cards' })
    }),
    [t]
  );

  if (isXs) {
    return (
      <MobileLayout>
        <Outlet />
      </MobileLayout>
    );
  }

  return (
    <DesktopTitledLayout title={t('ACCOUNT_AREA_TITLE', { defaultValue: 'Account' })}>
      <Suspense>
        <Navvy routes={routes} prefix="ACCOUNT" labels={labels} />
      </Suspense>
      <Outlet />
    </DesktopTitledLayout>
  );
}

Component.displayName = 'Account';
